import { useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, IconButton, MenuItem, Select } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';

import {
  ActionItem,
  Unit,
  UserData,
} from '@urbanmix-tech/shared-js';

import {
  convertPseudoDateToRealDate,
  getLongNumberStr,
} from '@/helpers/common';

import {
  ITableCell,
  ITableRow,
  VirtualizedTable,
  ColumnSeletion
} from '@/components/shared/Table';

// Redux
import { useDispatch } from 'react-redux';
import {
  setHighlightedUnits,
  setSelectedUnit,
  setApartmentData,
  setVisualizer,
  setHoveredUnitId,
} from '@/features/project/projectSlice';
import {
  ColorGradeVisualizer,
  ColorGradientVisualizer,
  Visualizer,
} from '@urbanmix-tech/um-embedded';
import { UnitMixEditModal } from '../UnitTabs/UnitMixEditModal';
import { useAuth } from '@/shared/context/AuthProvider';

import { GenericDistinctVisualizer,colorScales } from './BuildingVisualizers'
import type { ColorScaleOptions } from './BuildingVisualizers';
import { Label } from '@mui/icons-material';

type UnitMixTabContentProps = {
  units: Unit[];
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
};

type UnitTasksProperties = {
  hasOwnerActiveTasks: boolean;
  hasSupervisorActiveTasks: boolean;
  hasSupervisorLateTasks: boolean;
  hasFollowersActiveTasks: boolean;
  hasAnyActiveItems: boolean;
  hasLateTasks: boolean;
  activeUsers: UserData[];
  groupColor: string;
  groupColorClass: string;
  groupIcon: string;
};

export function getUnitTasksProperties(
  actionItems: ActionItem[],
  usersData: Record<string, UserData>,
  currentUser: UserData
): UnitTasksProperties {
  const result: UnitTasksProperties = {
    hasOwnerActiveTasks: false,
    hasSupervisorActiveTasks: false,
    hasSupervisorLateTasks: false,
    hasFollowersActiveTasks: false,
    hasAnyActiveItems: false,
    hasLateTasks: false,
    activeUsers: [],
    groupColor: '',
    groupColorClass: '',
    groupIcon: '',
  };
  const users = new Set<string>();

  actionItems.forEach((item) => {
    if (item.owner?.userId && item.status !== 'done') {
      users.add(item.owner.userId);
    }

    if (item.owner?.userId === currentUser.id && item.status !== 'done') {
      result.hasOwnerActiveTasks = true;
    }
    if (item.supervisor?.userId === currentUser.id && item.status !== 'done') {
      result.hasSupervisorActiveTasks = true;
    }
    const dueDate = convertPseudoDateToRealDate(item.dueDate) || new Date();
    if (
      item.supervisor?.userId === currentUser.id &&
      item.status !== 'done' &&
      dueDate < new Date()
    ) {
      result.hasSupervisorLateTasks = true;
    }
    if (
      item.followers &&
      item.followers.find((f) => f.userId === currentUser.id) &&
      item.status !== 'done'
    ) {
      result.hasFollowersActiveTasks = true;
    }
    if (item.status !== 'done') {
      result.hasAnyActiveItems = true;
    }
    if (dueDate < new Date()) {
      result.hasLateTasks = true;
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  result.activeUsers = Array.from(users).map(
    (userId: string) => usersData[userId]
  );
  return result;
}

export function UnitMixTabContent({
  units,
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
}: UnitMixTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { userData, isEditMode } = useAuth();
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  // Redux Selectors
  // const units = useSelector((state: RootState) => state.project.projectUnits);
  // const selectedColumnState = useSelector(
  //   (state: RootState) => state.project.unitMixSelectedColumnState
  // );

  const handleClickRow = (apt: any) => {        
    console.log('handle click row',apt);
    dispatch(setSelectedUnit({ apartmentId: apt.id }));
    // dispatch(setHighlightedUnits([{ apartmentId: apt.id }]));
    // dispatch(setSelectedUnit({ apartmentId: apt.id }));
    
  };

  const showEditColumn = userData?.isDev && isEditMode;

  const columns: ITableCell[] = useMemo(
    () => [
      ...(showEditColumn
        ? [
            {
              label: '',
              sortable: false,
              className: 'bg-gray-950 w-[40px]',
            },
          ]
        : []),
      {
        label: 'Unit',
        sortable: true,
        className: 'bg-gray-950',
      },
      {
        label: 'Rent Type',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
        kind: 'string',
      },
      {
        label: 'Type',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
        kind: 'string',
      },
      {
        label: 'Sq.Ft',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
      },
      {
        label: 'Lease End',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
      },
      {
        label: 'Rent',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
      },
    ],
    [showEditColumn]
  );

  useEffect(() => {
    dispatch(setHighlightedUnits(null));
  }, []);

  const rows: ITableRow[] = useMemo(() => {
    console.log('recalculating rows');
    return units.map((unit) => {
      const data = {
        ...(showEditColumn
          ? {
              '': {
                label: '',
                value: '',
                children: (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingUnit(unit);
                    }}
                    className="!text-stone-400 hover:!text-white"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                ),
              },
            }
          : {}),
        "Rent Type": {
          label: unit.affordable ? 'Affordable' : 'Market',
          value: unit.affordable ? 'Affordable' : 'Market',
          kind: 'string',
        },
        Unit: {
          label: unit?.name || unit.id,
          value: unit?.name || unit.id,
        },
        Type: {
          value:
          Number.isNaN(unit.numBedrooms) || +unit.numBedrooms === 0
          ? 'Studio'
          : `${unit.numBedrooms}B${
              Number.isNaN(unit.numBathrooms)
                ? ''
                : `/${unit.numBathrooms}B`
            }`,
          label:
            Number.isNaN(unit.numBedrooms) || +unit.numBedrooms === 0
              ? 'Studio'
              : `${unit.numBedrooms}B${
                  Number.isNaN(unit.numBathrooms)
                    ? ''
                    : `/${unit.numBathrooms}B`
                }`,
          kind: 'string',
        },
        'Sq.Ft': {
          label: `${Number.isNaN(unit.sqft) ? '-' : unit.sqft}`,
          value: Number.isNaN(unit.sqft) ? 0 : unit.sqft,
        },
        'Lease End': {
          label: unit.leaseEndDate
            ? // @ts-ignore
              dayjs(convertPseudoDateToRealDate(unit.leaseEndDate)).format('MM/DD/YYYY')
            : '',
          value: unit.leaseEndDate
            ? 
              // @ts-ignore
              dayjs(convertPseudoDateToRealDate(unit.leaseEndDate)).diff(dayjs(), 'day')
            : null,
        },
        Rent: {
          label: `$${getLongNumberStr(unit.actualRent || 0)}`,
          value: unit.actualRent || 0,
          className: 'border-r border-zinc-600',
        },
      } as Record<string, ITableCell>;

      return {
        id: unit.id,
        data,
      } as ITableRow;
    });
  }, [units, showEditColumn]);

  const [selectedScale, setSelectedScale] = useState<ColorScaleOptions>('default');
  const [selectedColumn, setSelectedColumn] = useState<ColumnSeletion | null>(null);

  useEffect(() => {
    let isNumbers = true;
    let column = selectedColumn;
    if (!column || column.value === false) {
      dispatch(setApartmentData([]));
      dispatch(setVisualizer(null));
      return;
    }
    let values = rows.map((row) => {
      if (row.data[column.label]['kind'] === 'string') {
        isNumbers = false;
        return (row.data[column.label].label ?? '') as string;
      }
      return +(row.data[column.label].value ?? 0);
    });

    const distinctValues = new Set(values);
    const colors = colorScales[selectedScale];

    let visualizer = null;
    // if less than 10 distinct values
    if (distinctValues.size < 10) {
      let colorMap = new Map();
      let arrDistinctValues = Array.from(distinctValues);
      arrDistinctValues.forEach((value, index) => {
        colorMap.set(value, colors[index]);
      });
      // console.log('setting visualizer for distinct values', colorMap);
      visualizer = new GenericDistinctVisualizer(colorMap);
    } else {
      if (!isNumbers) {
        visualizer = new ColorGradeVisualizer(colors, '', '');
      } else {
        const minValue = values.reduce((acc, value) => {
          if (value != 0) {
            return Math.min(acc as number, value as number);
          }
          return acc;
        }, Infinity);
        const maxValue = values.reduce((acc, value) => {
          return Math.max(acc as number, value as number);
        }, -Infinity);
        // console.log('setting visualizer for non distinct values', minValue,maxValue);
        // visualizer = new ColorGradientVisualizer(['#313695', '#A50026'], minValue, maxValue);
        visualizer = new ColorGradeVisualizer(colors, minValue, maxValue);
      }
    }

    let data = rows.map((row) => {
      // console.log('row', row.data['Unit'].value,row.data[column.label].label);
      return {
        apartmentId: row.data['Unit'].value,
        value:
          row.data[column.label].kind == 'string'
            ? row.data[column.label].label
            : row.data[column.label].value,
        label: column.label,
      };
    });

    dispatch(setApartmentData(data));
    dispatch(setVisualizer(visualizer));
  }, [selectedColumn, selectedScale]);

  return (
    <div className="unit-mix h-[calc(100vh-112px)] w-full" ref={ref}>
      <div className="postion-absoulte right-0 top-0 z-10 mb-6 flex flex-row gap-12">
        <div>
          <FormControl>
            <Select
              value={selectedScale}
              onChange={(e) => {
                setSelectedScale(e.target.value as ColorScaleOptions);
              }}
              label="Color Scale"
              size="xs"
              variant="standard"
            >
              <MenuItem value="blues">Blues</MenuItem>
              <MenuItem value="default">Green to Maroon</MenuItem>
              <MenuItem value="purpleToYellow">Purple to Yellow</MenuItem>
              <MenuItem value="tealToMagenta">Teal to Magenta</MenuItem>
              <MenuItem value="earthTones">Earth Tones</MenuItem>
              <MenuItem value="rainbow">Rainbow</MenuItem>
              <MenuItem value="pastel">Pastel</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
      <VirtualizedTable
        rows={rows}
        columns={columns}
        px={8}
        onClickRow={handleClickRow}
        hoveredRowId={hoveredUnitId || undefined}
        onChangeHover={(rowId) => {
          // console.log('on change hover')
          onHoverUnit(rowId);
          dispatch(setHoveredUnitId(rowId));
        }}
        classNames={{
          container:
            'rounded-none border-0 border-b border-b-solid border-zinc-600',
        }}
        onColumnSelected={(column) => {
          setSelectedColumn(column);
        }}
      />
      {editingUnit && (
        <UnitMixEditModal
          isModalOpen={!!editingUnit}
          onClose={() => setEditingUnit(null)}
          unit={editingUnit}
        />
      )}
    </div>
  );
}

class LeaseExpiritionVisualizer extends ColorGradeVisualizer {
  constructor() {
    super(['#313695', '#FFFFE5', '#A50026'], 60, 365);
  }

  getColor(value: any) {
    value = parseInt(value);

    if (value < 0) {
      return '#ff0000';
    } else if (value < 30) {
      return '#ff9900';
    } else if (value < 60) {
      return '#ffff00';
    } else {
      // if value is a number

      if (typeof value === 'number') {
        return super.getColor(value);
      } else {
        return '#0000FF';
      }
    }
  }
}
